import React, { useState, useEffect, useCallback } from 'react';
import { Form, Container, Row, Col, Spinner, InputGroup } from 'react-bootstrap';
import { signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { auth, signInWithGoogle } from '../firebase_utils/firebase';
import '../../styles/components/user/Login.css';
import useApi from '../../Api';
import Signup from './Signup';
import { useAuth } from '../../AuthContext';
import { validateTextInput } from '../common/TextValidator';
import Popup from '../common/popup/Popup';
import { FaEnvelope, FaLock } from 'react-icons/fa';
import { toast } from 'react-toastify';
import TermsDisclaimer from '../common/TermsDisclaimer';

const Login = ({ show, handleClose, openLogin, switchToVerify }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [showSignupModal, setShowSignupModal] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const apiClient = useApi();
  const { logout, setShowUsernameModal } = useAuth();

  const handleLogin = useCallback(async (emailInput, passwordInput) => {
    setLoading(true);
    setError('');
    setMessage('');
    try {
      await signInWithEmailAndPassword(auth, emailInput, passwordInput);
      handleClose();
    } catch (err) {
      console.error('Login error:', err);
      setError('Invalid email or password. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [handleClose]);

  const handleGoogleLogin = useCallback(async () => {
    setLoading(true);
    setError('');
    setMessage('');
    try {
      const userCredential = await signInWithGoogle();
      const token = await userCredential.getIdToken();
      const response = await apiClient.post('/google-signin', null, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.data.newUser) {
        handleClose();
        setShowUsernameModal(true);
      } else {
        handleClose();
      }
    } catch (error) {
      if (error.code !== 'auth/popup-closed-by-user') {
        console.error('Google login error:', error);
        toast.error('Login failed. Please try again.', {
          position: "top-center",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        try {
          await logout();
        } catch (logoutError) {
          console.error('Logout error:', logoutError);
        }
      } else {
        setError('');
      }
    } finally {
      setLoading(false);
    }
  }, [apiClient, handleClose, logout, setShowUsernameModal]);

  const onSubmitLogin = (e) => {
    e.preventDefault();
    if (!validateInputs()) {
      return;
    }
    handleLogin(email, password);
  };

  const validateInputs = () => {
    let valid = true;
    let errorMessages = [];
    if (email.trim() !== '') {
      const emailValidation = validateTextInput(email, {
        maxLength: 255,
        allowSpecialChars: true,
      });
      if (!emailValidation.isValid) {
        valid = false;
        errorMessages.push(...emailValidation.errors.map((err) => `Email: ${err}`));
      }
    } else {
      valid = false;
      errorMessages.push('Email: Email is required.');
    }
    if (password === '') {
      valid = false;
      errorMessages.push('Password: Password is required.');
    }
    if (!valid) {
      setError(errorMessages.join(' '));
    } else {
      setError('');
    }
    return valid;
  };

  const handlePasswordReset = async () => {
    if (!email) {
      setError('Please enter your email to reset the password.');
      return;
    }
    const emailValidation = validateTextInput(email, {
      maxLength: 255,
      allowSpecialChars: true,
    });
    if (!emailValidation.isValid) {
      setError(emailValidation.errors.join(' '));
      return;
    }
    setLoading(true);
    setError('');
    setMessage('');
    try {
      await sendPasswordResetEmail(auth, email);
      setMessage('If an account with this email exists, a password reset email has been sent.');
    } catch (err) {
      setMessage('If an account with this email exists, a password reset email has been sent.');
    } finally {
      setLoading(false);
    }
  };

  const handleSignupLinkClick = () => {
    handleClose();
    setShowSignupModal(true);
  };

  const switchToLogin = () => {
    setShowSignupModal(false);
    if (typeof openLogin === 'function') {
      openLogin();
    }
  };

  useEffect(() => {
    if (!show) {
      setEmail('');
      setPassword('');
      setError('');
      setMessage('');
      setShowEmailForm(false);
    }
  }, [show]);

  return (
    <>
      {show && (
        <Popup title="Sign In To WeiFinders" onCancel={handleClose} className="popup-modal login-modal">
          <Container>
            <Row className="mb-3">
              <Col>
                <button
                  className="gsi-material-button w-100"
                  onClick={handleGoogleLogin}
                  disabled={loading}
                >
                  <div className="gsi-material-button-state"></div>
                  <div className="gsi-material-button-content-wrapper">
                    <div className="gsi-material-button-icon">
                      <svg version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" style={{display: 'block'}}>
                        <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"></path>
                        <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"></path>
                        <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"></path>
                        <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"></path>
                        <path fill="none" d="M0 0h48v48H0z"></path>
                      </svg>
                    </div>
                    <span className="gsi-material-button-contents">
                      {loading ? <Spinner animation="border" size="sm" /> : 'Continue with Google'}
                    </span>
                    <span style={{display: 'none'}}>Continue with Google</span>
                  </div>
                </button>
              </Col>
            </Row>
            <Row>
              <Col>
                <button
                  className="gsi-material-button email-login-button w-100"
                  onClick={() => setShowEmailForm(!showEmailForm)}
                  disabled={loading}
                >
                  <div className="gsi-material-button-state"></div>
                  <div className="gsi-material-button-content-wrapper">
                    <div className="gsi-material-button-icon"><FaEnvelope /></div>
                    <span className="gsi-material-button-contents">Continue with Email</span>
                    <span style={{display: 'none'}}>Continue with Email</span>
                  </div>
                </button>
              </Col>
            </Row>
            {showEmailForm && (
              <>
                <hr className="hr-custom" />
                <Form onSubmit={onSubmitLogin}>
                  <Form.Group controlId="formEmail" className="mb-3">
                    <InputGroup>
                      <InputGroup.Text className="input-icon"><FaEnvelope /></InputGroup.Text>
                      <Form.Control
                        type="email"
                        placeholder="Email"
                        value={email}
                        onChange={(e) => { setEmail(e.target.value); if(error) setError(''); }}
                        required
                        className="form-input"
                        disabled={loading}
                      />
                    </InputGroup>
                  </Form.Group>
                  <Form.Group controlId="formPassword" className="mb-3">
                    <InputGroup>
                      <InputGroup.Text className="input-icon"><FaLock /></InputGroup.Text>
                      <Form.Control
                        type="password"
                        placeholder="Password"
                        value={password}
                        onChange={(e) => { setPassword(e.target.value); if(error) setError(''); }}
                        required
                        className="form-input"
                        disabled={loading}
                      />
                    </InputGroup>
                  </Form.Group>
                  {error && <p className="text-danger">{error}</p>}
                  {message && <p className="text-neutral">{message}</p>}
                  <div className="d-grid gap-2">
                    <button className="w-100 btn-neutral" type="submit" disabled={loading}>
                      {loading ? <Spinner animation="border" size="sm" /> : 'Sign In'}
                    </button>
                  </div>
                </Form>
                <p className="mt-3 text-center">
                  <a
                    href="/forgot-password"
                    onClick={(e) => { e.preventDefault(); handlePasswordReset(); }}
                    className="forgot-password-link"
                  >
                    Forgot your password?
                  </a>
                </p>
                <p className="w-100 text-center mt-3">
                  Don't have an account?{' '}
                  <a
                    href="/signup"
                    onClick={(e) => { e.preventDefault(); handleSignupLinkClick(); }}
                    className="signup-link"
                  >
                    Sign up here
                  </a>
                </p>
              </>
            )}
            <TermsDisclaimer />
          </Container>
        </Popup>
      )}
      <Signup show={showSignupModal} handleClose={() => setShowSignupModal(false)} switchToLogin={switchToLogin} />
    </>
  );
};

export default Login;
